import React from 'react';
import { ExpandIcon } from '../../icons/navigation';

interface CollapsibleSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  side: 'left' | 'right';
  children: React.ReactNode;
  collapsedContent?: React.ReactNode;
}

const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  isCollapsed,
  onToggle,
  side,
  children,
  collapsedContent,
}) => {
  const collapsedView = collapsedContent || (
    <div
      className={`w-12 h-full bg-white dark:bg-gray-900 ${side === 'left' ? 'border-r' : 'border-l'} border-gray-200 dark:border-gray-700 flex flex-col items-center py-4 hidden md:flex`}
    >
      <button
        onClick={onToggle}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title={`Expand ${side} sidebar`}
      >
        <ExpandIcon
          size={20}
          className={`text-gray-600 dark:text-gray-300 ${side === 'right' ? 'scale-x-[-1]' : ''}`}
        />
      </button>
    </div>
  );

  const mobilePosition = side === 'left' ? 'left-0' : 'right-0';

  const mobileSlideAnimation =
    side === 'left'
      ? isCollapsed
        ? '-translate-x-full'
        : 'translate-x-0'
      : isCollapsed
        ? 'translate-x-full'
        : 'translate-x-0';

  return (
    <div
      className={`transition-all duration-300 ${isCollapsed ? 'w-0 md:w-12' : 'w-0 md:w-96'}`}
    >
      {/* Desktop collapsed view */}
      {isCollapsed && (
        <div className="hidden md:block w-full h-full">{collapsedView}</div>
      )}

      {/* Mobile overlay backdrop */}
      <div
        className={`fixed inset-0 bg-black md:hidden z-30 transition-opacity duration-300 ${
          isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-50'
        }`}
        onClick={onToggle}
      />

      {/* Sidebar content - Always rendered on mobile for animation, conditionally on desktop */}
      <div
        className={`fixed top-0 ${mobilePosition} bottom-0 w-full max-w-sm z-40 transform transition-transform duration-300 ${mobileSlideAnimation} md:relative md:inset-auto md:max-w-none md:translate-x-0 h-full ${
          isCollapsed ? 'md:hidden' : 'md:block'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default CollapsibleSidebar;
